import React, { useState, useMemo } from 'react';
import {
    Box,
    Container,
    Typography,
    Alert,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Tabs,
    Tab,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import StorageIcon from '@mui/icons-material/Storage';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';
import { companyApi } from '../services/api/companyApi';
import ApiReferenceTab from '../components/docs/ApiReferenceTab';
import AnalyticsApiTab from '../components/docs/AnalyticsApiTab';
import PayloadExecutor from '../components/docs/PayloadExecutor';
import PageHeader from '../components/common/PageHeader';

// Load locally computed examples
import testCasesData from '../data/apiExamples.json';


const ApiDocsPage: React.FC = () => {
    const { user } = useAuthStore();

    // Top-level page tab: 0 = Payload Explorer, 1 = API Reference
    const [pageTab, setPageTab] = useState(0);

    // Explorer State
    const [selectedVersion, setSelectedVersion] = useState<string>('3.2');
    const [selectedLegType, setSelectedLegType] = useState<string>('');
    const [selectedScenarioIndex, setSelectedScenarioIndex] = useState<number>(0);

    // API key for PayloadExecutor
    const [apiKey, setApiKey] = useState('');
    React.useEffect(() => {
        if (user?.companyId) {
            companyApi.getById(user.companyId)
                .then((c: any) => setApiKey(c.apiKey || c.api_key || ''))
                .catch(() => { });
        }
    }, [user?.companyId]);

    // Memos for dropdown populators
    const versions = useMemo(() => Object.keys(testCasesData).filter(v => v !== '2.8').sort((a, b) => a.localeCompare(b)).reverse(), []);

    const legTypes = useMemo(() => {
        if (!testCasesData[selectedVersion as keyof typeof testCasesData]) return [];
        return Object.keys(testCasesData[selectedVersion as keyof typeof testCasesData]).sort((a, b) => a.localeCompare(b));
    }, [selectedVersion]);

    // Update leg type when version changes
    React.useEffect(() => {
        if (legTypes.length > 0 && (!selectedLegType || !legTypes.includes(selectedLegType))) {
            setSelectedLegType(legTypes[0]);
        }
    }, [legTypes, selectedLegType]);

    const scenarios = useMemo(() => {
        if (!selectedLegType || !testCasesData[selectedVersion as keyof typeof testCasesData]) return [];
        const typesMap = (testCasesData as any)[selectedVersion] || {};
        return typesMap[selectedLegType] || [];
    }, [selectedVersion, selectedLegType]);

    // Reset scenario index when leg type changes
    React.useEffect(() => {
        setSelectedScenarioIndex(0);
    }, [scenarios]);

    // Build PayloadSample from the selected scenario
    const currentSample = useMemo(() => {
        const s = scenarios[selectedScenarioIndex];
        if (!s) return null;
        return {
            json: JSON.stringify(s.restRequest, null, 2),
            xml: s.xmlRequest as string,
        };
    }, [scenarios, selectedScenarioIndex]);

    if (user?.accountType! < AccountType.User) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ mt: 4 }}>
                    <Alert severity="error">
                        Access denied. This feature is only available to authorized users.
                    </Alert>
                </Box>
            </Container>
        );
    }

    const currentScenario = scenarios[selectedScenarioIndex] || null; // used for sidebar info card

    return (
        <Box sx={{ mb: 4 }}>
            <PageHeader description="Reference for integrating with the Carbon Care API - explore 90+ scenarios with JSON or XML payloads." />
            <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={pageTab}
                        onChange={(_, v) => setPageTab(v)}
                        textColor="primary"
                        indicatorColor="primary"
                    >
                        <Tab
                            icon={<CodeIcon fontSize="small" />}
                            iconPosition="start"
                            label="Payload Explorer"
                            sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
                        />
                        <Tab
                            icon={<MenuBookIcon fontSize="small" />}
                            iconPosition="start"
                            label="API Reference"
                            sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
                        />
                        <Tab
                            icon={<StorageIcon fontSize="small" />}
                            iconPosition="start"
                            label="Analytics API"
                            sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
                        />
                    </Tabs>
                </Box>

            {/* API Reference Tab (Schema + Swagger toggle) */}
            {pageTab === 1 && (
                <Box sx={{ mt: 1 }}>
                    <ApiReferenceTab apiKey={apiKey} />
                </Box>
            )}

            {/* Analytics API Tab */}
            {pageTab === 2 && (
                <Box sx={{ mt: 1 }}>
                    <AnalyticsApiTab apiKey={apiKey} />
                </Box>
            )}

            {/* Payload Explorer Tab */}
            {pageTab === 0 && (
                <Grid container spacing={4}>
                    <Grid item xs={12} md={3}>
                        <Card variant="outlined" sx={{ height: '100%', borderColor: 'primary.light' }}>
                            <CardContent>
                                <Typography variant="subtitle1" gutterBottom color="primary.main" fontWeight={600} display="flex" alignItems="center" gap={1}>
                                    <CodeIcon fontSize="small" /> Scenario Explorer
                                </Typography>

                                <FormControl fullWidth size="small" sx={{ mb: 3, mt: 2 }}>
                                    <InputLabel>API Version</InputLabel>
                                    <Select
                                        value={selectedVersion}
                                        label="API Version"
                                        onChange={(e) => setSelectedVersion(e.target.value)}
                                    >
                                        {versions.map(v => <MenuItem key={v} value={v}>Version {v}</MenuItem>)}
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                                    <InputLabel>Leg Type (Mode)</InputLabel>
                                    <Select
                                        value={selectedLegType}
                                        label="Leg Type (Mode)"
                                        onChange={(e) => setSelectedLegType(e.target.value)}
                                    >
                                        {legTypes.map(lt => <MenuItem key={lt} value={lt}>{lt}</MenuItem>)}
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                    <InputLabel>Select Scenario</InputLabel>
                                    <Select
                                        value={selectedScenarioIndex}
                                        label="Select Scenario"
                                        onChange={(e) => setSelectedScenarioIndex(Number(e.target.value))}
                                    >
                                        {scenarios.map((s: any, idx: number) => (
                                            <MenuItem key={s.id} value={idx} sx={{ whiteSpace: 'normal', fontSize: 14 }}>
                                                {s.description}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {currentScenario && (
                                    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'rgba(144, 191, 69, 0.05)', borderRadius: 1 }}>
                                        <Typography variant="caption" color="textSecondary" display="block">Internal ID</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{currentScenario.id}</Typography>
                                    </Box>
                                )}


                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={9}>
                        {currentSample ? (
                            <Card variant="outlined">
                                <CardContent>
                                    <PayloadExecutor samples={currentSample} apiKey={apiKey} />
                                </CardContent>
                            </Card>
                        ) : (
                            <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa', borderRadius: 1, border: '1px dashed #ccc' }}>
                                <Typography color="textSecondary">No scenarios found for this combination.</Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default ApiDocsPage;
